package features.booking

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import abi.composeapp.generated.resources.Res
import abi.composeapp.generated.resources.logotipo_smartvan

@Composable
fun ChoferScreen(
    viewModel: ChoferViewModel,
    nombreChofer: String,
    onLogout: () -> Unit,
    // Cuando admin+ ve el panel de un chofer específico (no es su propio panel).
    // Si estos vienen no-nulos → modo espectador (cargar viajes del target + botón "Volver al admin").
    impersonarUid: String? = null,
    impersonarNombre: String? = null,
    onSalirEspectador: (() -> Unit)? = null
) {
    val isLoading by viewModel.isLoading.collectAsState()
    val viajesHoy by viewModel.viajesHoy.collectAsState()
    val viajesManana by viewModel.viajesManana.collectAsState()
    val viajeSeleccionado by viewModel.viajeSeleccionado.collectAsState()
    val esEspectador = impersonarUid != null && impersonarNombre != null

    // Feedback visual de operaciones del VM (ej. Cobrar en efectivo). Sin esto el chofer
    // presionaba "Cobrar" y no sabía si funcionó o falló el guard de 1h.
    val mensajeChofer by viewModel.mensajeChofer.collectAsState()
    mensajeChofer?.let { msg ->
        AlertDialog(
            onDismissRequest = { viewModel.limpiarMensaje() },
            title = { Text(if (msg.startsWith("Error") || msg.contains("no puedes") || msg.contains("No se")) "Aviso" else "Listo") },
            text = { Text(msg) },
            confirmButton = {
                TextButton(onClick = { viewModel.limpiarMensaje() }) { Text("Entendido") }
            }
        )
    }

    LaunchedEffect(nombreChofer, impersonarUid, impersonarNombre) {
        if (esEspectador) {
            viewModel.cargarViajesImpersonando(impersonarUid!!, impersonarNombre!!)
        } else {
            viewModel.limpiarImpersonacion()
            viewModel.cargarViajesDelChofer(nombreChofer)
        }
    }

    if (viajeSeleccionado != null) {
        DetalleViajeChofer(
            viaje = viajeSeleccionado!!,
            viewModel = viewModel,
            onRegresar = { viewModel.regresarAMisViajes() },
            esEspectador = esEspectador
        )
    } else {
        PanelChofer(
            nombreChofer = viewModel.nombreChofer.value,
            isLoading = isLoading,
            viajesHoy = viajesHoy,
            viajesManana = viajesManana,
            onSeleccionarViaje = { viewModel.seleccionarViaje(it) },
            onRefrescar = { viewModel.refrescarViajes() },
            onLogout = if (esEspectador) (onSalirEspectador ?: onLogout) else onLogout,
            esEspectador = esEspectador
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PanelChofer(
    nombreChofer: String,
    isLoading: Boolean,
    viajesHoy: List<ViajeChofer>,
    viajesManana: List<ViajeChofer>,
    onSeleccionarViaje: (ViajeChofer) -> Unit,
    onRefrescar: () -> Unit,
    onLogout: () -> Unit,
    esEspectador: Boolean = false
) {
    val drawerState = rememberDrawerState(DrawerValue.Closed)
    val scope = rememberCoroutineScope()
    val smartvanDark = SmartVanColors.AzulOscuro
    val smartvanCyan = SmartVanColors.CyanAccent

    ModalNavigationDrawer(
        drawerState = drawerState,
        drawerContent = {
            ModalDrawerSheet(
                modifier = Modifier.width(280.dp),
                drawerContainerColor = Color.White
            ) {
                Column(
                    modifier = Modifier.fillMaxHeight()
                ) {
                    // Drawer header with logo
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(smartvanDark)
                    ) {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 24.dp, vertical = 28.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Box(
                                modifier = Modifier
                                    .background(Color.White, CircleShape)
                                    .padding(10.dp)
                            ) {
                                Image(
                                    painter = painterResource(Res.drawable.logotipo_smartvan),
                                    contentDescription = "SmartVan",
                                    modifier = Modifier.size(height = 50.dp, width = 80.dp)
                                )
                            }

                            Spacer(modifier = Modifier.height(16.dp))

                            // Avatar
                            Box(
                                modifier = Modifier
                                    .size(52.dp)
                                    .clip(CircleShape)
                                    .background(smartvanCyan),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = nombreChofer.firstOrNull()?.uppercase() ?: "C",
                                    fontSize = 22.sp,
                                    fontWeight = FontWeight.Black,
                                    color = Color.White
                                )
                            }

                            Spacer(modifier = Modifier.height(10.dp))
                            Text(
                                nombreChofer,
                                color = Color.White,
                                fontWeight = FontWeight.Bold,
                                fontSize = 17.sp
                            )
                            Text(
                                "CHOFER",
                                color = smartvanCyan,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.SemiBold
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    Text(
                        "NAVEGACIÓN",
                        color = Color.Gray,
                        style = MaterialTheme.typography.labelSmall,
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                    )

                    NavigationDrawerItem(
                        icon = { Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = smartvanCyan) },
                        label = { Text("Mis Viajes") },
                        selected = true,
                        onClick = { scope.launch { drawerState.close() } },
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 2.dp),
                        colors = NavigationDrawerItemDefaults.colors(
                            selectedContainerColor = smartvanCyan.copy(alpha = 0.1f),
                            selectedTextColor = smartvanDark,
                            selectedIconColor = smartvanCyan
                        )
                    )

                    NavigationDrawerItem(
                        icon = { Icon(Icons.Filled.Refresh, contentDescription = null, tint = Color.Gray) },
                        label = { Text("Actualizar Viajes") },
                        selected = false,
                        onClick = {
                            onRefrescar()
                            scope.launch { drawerState.close() }
                        },
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 2.dp)
                    )

                    Spacer(modifier = Modifier.weight(1f))
                    HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))

                    NavigationDrawerItem(
                        icon = { Icon(Icons.Filled.ExitToApp, contentDescription = null, tint = Color.Red) },
                        label = { Text("Cerrar Sesión", color = Color.Red) },
                        selected = false,
                        onClick = {
                            scope.launch { drawerState.close() }
                            onLogout()
                        },
                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp)
                    )

                    Spacer(modifier = Modifier.height(12.dp))
                }
            }
        }
    ) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = {
                        Column {
                            Text(
                                "Hola, $nombreChofer",
                                fontWeight = FontWeight.Bold,
                                fontSize = 17.sp,
                                color = Color.White
                            )
                            Text(
                                "Panel de Chofer",
                                fontSize = 12.sp,
                                color = Color.White.copy(alpha = 0.7f)
                            )
                        }
                    },
                    navigationIcon = {
                        IconButton(onClick = { scope.launch { drawerState.open() } }) {
                            Icon(Icons.Filled.Menu, contentDescription = "Menú", tint = Color.White)
                        }
                    },
                    actions = {
                        IconButton(onClick = onRefrescar) {
                            Icon(Icons.Filled.Refresh, contentDescription = "Refrescar", tint = Color.White)
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = smartvanDark
                    )
                )
            }
        ) { paddingValues ->
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .background(SmartVanColors.FondoClaro)
                    .padding(paddingValues)
                    .padding(16.dp)
            ) {
                if (esEspectador) {
                    Card(
                        colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                        shape = RoundedCornerShape(10.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Filled.Visibility, contentDescription = null, tint = Color(0xFF6D4C00), modifier = Modifier.size(18.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Column(modifier = Modifier.weight(1f)) {
                                Text("Modo espectador", fontWeight = FontWeight.Bold, color = Color(0xFF6D4C00), style = MaterialTheme.typography.bodyMedium)
                                Text("Estás viendo el panel de $nombreChofer. Acciones deshabilitadas.", style = MaterialTheme.typography.bodySmall, color = Color(0xFF6D4C00))
                            }
                            TextButton(onClick = onLogout) { Text("Salir", color = Color(0xFF6D4C00), fontWeight = FontWeight.Bold) }
                        }
                    }
                    Spacer(modifier = Modifier.height(10.dp))
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    ResumenCard(
                        modifier = Modifier.weight(1f),
                        titulo = "Hoy",
                        cantidad = viajesHoy.size,
                        color = SmartVanColors.Verde,
                        icon = Icons.Filled.DirectionsBus
                    )
                    ResumenCard(
                        modifier = Modifier.weight(1f),
                        titulo = "Mañana",
                        cantidad = viajesManana.size,
                        color = SmartVanColors.Azul,
                        icon = Icons.Filled.CalendarToday
                    )
                    ResumenCard(
                        modifier = Modifier.weight(1f),
                        titulo = "Pasajeros",
                        cantidad = viajesHoy.sumOf { it.totalAsientos },
                        color = SmartVanColors.Naranja,
                        icon = Icons.Filled.People
                    )
                }

                Spacer(modifier = Modifier.height(20.dp))

                if (isLoading) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = smartvanCyan)
                    }
                } else {
                    LazyColumn(
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                        modifier = Modifier.fillMaxSize()
                    ) {
                        // Hoy section
                        item {
                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(bottom = 4.dp)) {
                                Box(
                                    modifier = Modifier.size(8.dp).background(SmartVanColors.Verde, CircleShape)
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(
                                    "Viajes de Hoy",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = smartvanDark
                                )
                            }
                        }

                        if (viajesHoy.isEmpty()) {
                            item {
                                Card(
                                    colors = CardDefaults.cardColors(containerColor = Color.White),
                                    shape = RoundedCornerShape(14.dp),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Row(
                                        modifier = Modifier.padding(20.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Icon(Icons.Filled.EventBusy, contentDescription = null, tint = Color.LightGray, modifier = Modifier.size(28.dp))
                                        Spacer(modifier = Modifier.width(12.dp))
                                        Text(
                                            "No tienes viajes asignados para hoy",
                                            color = Color.Gray,
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                }
                            }
                        } else {
                            items(viajesHoy) { viaje ->
                                ViajeChoferCard(viaje = viaje, onClick = { onSeleccionarViaje(viaje) })
                            }
                        }

                        // Mañana section
                        item {
                            Spacer(modifier = Modifier.height(8.dp))
                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(bottom = 4.dp)) {
                                Box(
                                    modifier = Modifier.size(8.dp).background(SmartVanColors.Azul, CircleShape)
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(
                                    "Viajes de Mañana",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = SmartVanColors.Azul
                                )
                            }
                        }

                        if (viajesManana.isEmpty()) {
                            item {
                                Card(
                                    colors = CardDefaults.cardColors(containerColor = Color.White),
                                    shape = RoundedCornerShape(14.dp),
                                    modifier = Modifier.fillMaxWidth()
                                ) {
                                    Row(
                                        modifier = Modifier.padding(20.dp),
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Icon(Icons.Filled.EventBusy, contentDescription = null, tint = Color.LightGray, modifier = Modifier.size(28.dp))
                                        Spacer(modifier = Modifier.width(12.dp))
                                        Text(
                                            "No tienes viajes asignados para mañana",
                                            color = Color.Gray,
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                }
                            }
                        } else {
                            items(viajesManana) { viaje ->
                                ViajeChoferCard(viaje = viaje, onClick = { onSeleccionarViaje(viaje) })
                            }
                        }

                        item { Spacer(modifier = Modifier.height(16.dp)) }
                    }
                }
            }
        }
    }
}

@Composable
fun ViajeChoferCard(viaje: ViajeChofer, onClick: () -> Unit) {
    val smartvanCyan = SmartVanColors.CyanAccent
    val smartvanDark = SmartVanColors.AzulOscuro

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() },
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        shape = RoundedCornerShape(14.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.weight(1f)) {
                    Box(
                        modifier = Modifier
                            .size(42.dp)
                            .clip(CircleShape)
                            .background(if (viaje.esHoy) smartvanCyan.copy(alpha = 0.1f) else SmartVanColors.Azul.copy(alpha = 0.1f)),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            Icons.Filled.DirectionsBus,
                            contentDescription = null,
                            tint = if (viaje.esHoy) smartvanCyan else SmartVanColors.Azul,
                            modifier = Modifier.size(22.dp)
                        )
                    }
                    Spacer(modifier = Modifier.width(12.dp))
                    Column {
                        Text(
                            viaje.ruta,
                            fontWeight = FontWeight.Bold,
                            fontSize = 15.sp,
                            color = smartvanDark
                        )
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Filled.Schedule, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp))
                            Spacer(modifier = Modifier.width(4.dp))
                            Text(viaje.horario, color = Color.Gray, fontSize = 13.sp)
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(viaje.fecha, fontSize = 12.sp, color = Color.Gray)
                        }
                    }
                }

                // Passenger count badge
                Box(
                    modifier = Modifier
                        .background(
                            if (viaje.totalAsientos > 0) SmartVanColors.Verde.copy(alpha = 0.1f) else SmartVanColors.FondoGrisClaro,
                            RoundedCornerShape(20.dp)
                        )
                        .padding(horizontal = 10.dp, vertical = 6.dp)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.People, contentDescription = null, tint = if (viaje.totalAsientos > 0) SmartVanColors.Verde else Color.Gray, modifier = Modifier.size(14.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(
                            "${viaje.totalAsientos}",
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp,
                            color = if (viaje.totalAsientos > 0) SmartVanColors.Verde else Color.Gray
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(10.dp))

            // Vehicle + action row
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // weight(1f) + maxLines: sin esto el vehículo + la lista de puntos de subida
                // encalaban "Ver manifiesto" en pantallas angostas.
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.weight(1f)) {
                    Icon(Icons.Filled.LocalShipping, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(15.dp))
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(viaje.vehiculo, fontSize = 12.sp, color = Color.Gray, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    if (viaje.puntoSubida.isNotBlank()) {
                        Spacer(modifier = Modifier.width(8.dp))
                        Icon(Icons.Filled.LocationOn, contentDescription = null, tint = SmartVanColors.Azul, modifier = Modifier.size(13.dp))
                        Spacer(modifier = Modifier.width(2.dp))
                        // Puntos separados por "|" a nivel viaje: se muestran con coma y una línea.
                        Text(
                            viaje.puntoSubida.replace("|", ", "),
                            fontSize = 11.sp, color = SmartVanColors.Azul, fontWeight = FontWeight.Medium,
                            maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f, fill = false)
                        )
                    }
                }
                Spacer(modifier = Modifier.width(8.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        "Ver manifiesto",
                        fontSize = 12.sp,
                        color = smartvanCyan,
                        fontWeight = FontWeight.Bold,
                        maxLines = 1
                    )
                    Icon(Icons.Filled.ChevronRight, contentDescription = null, tint = smartvanCyan, modifier = Modifier.size(18.dp))
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DetalleViajeChofer(viaje: ViajeChofer, viewModel: ChoferViewModel, onRegresar: () -> Unit, esEspectador: Boolean = false) {
    val scope = rememberCoroutineScope()
    // Coordenadas de subidas/bajadas para abrir cada punto (y la ruta) en Maps.
    val mapsPorPunto by viewModel.mapsPorPunto.collectAsState()
    val uriHandlerChofer = LocalUriHandler.current
    LaunchedEffect(Unit) { viewModel.cargarUbicacionesDePuntos() }
    var asistencia by remember { mutableStateOf<Map<Int, Boolean>>(emptyMap()) }
    var mostrarResultadoQR by remember { mutableStateOf<VerificacionResult?>(null) }
    var codigoManual by remember { mutableStateOf("") }
    var mostrarInputManual by remember { mutableStateOf(false) }
    var mostrarEscaner by remember { mutableStateOf(false) }
    var mostrarConfirmIniciar by remember { mutableStateOf(false) }
    var mostrarConfirmFinalizar by remember { mutableStateOf(false) }
    val smartvanDark = SmartVanColors.AzulOscuro
    val smartvanCyan = SmartVanColors.CyanAccent

    val rastreoActivo by viewModel.rastreoActivo.collectAsState()
    val estadoViaje by viewModel.estadoViaje.collectAsState()
    val ultimaUbicacion by viewModel.ultimaUbicacion.collectAsState()

    LaunchedEffect(viaje) {
        asistencia = viewModel.obtenerAsistencia(viaje)
        if (viaje.esHoy) {
            viewModel.cargarEstadoRastreo(viaje)
        }
    }

    if (mostrarEscaner) {
        org.example.abigail.QRScannerScreen(
            onCodigoEscaneado = { codigo ->
                mostrarEscaner = false
                scope.launch {
                    val resultado = viewModel.verificarCodigoQR(codigo)
                    mostrarResultadoQR = resultado
                    if (resultado is VerificacionResult.Exito) {
                        asistencia = asistencia + (resultado.asiento to true)
                    }
                    if (resultado is VerificacionResult.PagoPendiente) {
                        asistencia = asistencia + (resultado.asiento to true)
                    }
                }
            },
            onCerrar = { mostrarEscaner = false }
        )
        return
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Manifiesto de Pasajeros", fontWeight = FontWeight.Bold, color = Color.White) },
                navigationIcon = {
                    IconButton(onClick = onRegresar) {
                        Icon(Icons.Filled.ArrowBack, contentDescription = "Regresar", tint = Color.White)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = smartvanDark
                )
            )
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(SmartVanColors.FondoClaro)
                .padding(paddingValues)
                .verticalScroll(rememberScrollState())
                .padding(16.dp)
        ) {
            // Trip info card
            Card(
                colors = CardDefaults.cardColors(containerColor = smartvanDark),
                shape = RoundedCornerShape(16.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Column(modifier = Modifier.padding(18.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.DirectionsBus, contentDescription = null, tint = smartvanCyan, modifier = Modifier.size(22.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(viaje.ruta, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 17.sp)
                    }
                    Spacer(modifier = Modifier.height(14.dp))
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Column {
                            Text("Fecha", color = Color.White.copy(alpha = 0.6f), fontSize = 11.sp)
                            Text(viaje.fecha, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 14.sp)
                        }
                        Column {
                            Text("Horario", color = Color.White.copy(alpha = 0.6f), fontSize = 11.sp)
                            Text(viaje.horario, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 14.sp)
                        }
                        Column {
                            Text("Vehículo", color = Color.White.copy(alpha = 0.6f), fontSize = 11.sp)
                            Text(viaje.vehiculo, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 14.sp)
                        }
                    }

                    if (viaje.puntoSubida.isNotBlank()) {
                        Spacer(modifier = Modifier.height(8.dp))
                        Card(
                            colors = CardDefaults.cardColors(containerColor = Color.White.copy(alpha = 0.15f)),
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Row(modifier = Modifier.padding(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Filled.LocationOn, contentDescription = null, tint = Color(0xFF81C784), modifier = Modifier.size(16.dp))
                                Spacer(modifier = Modifier.width(6.dp))
                                Column {
                                    Text("PUNTO DE SUBIDA", color = Color.White.copy(alpha = 0.6f), fontSize = 10.sp, fontWeight = FontWeight.Bold)
                                    Text(viaje.puntoSubida, color = Color.White, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                                }
                            }
                        }
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    // Progress bar
                    val confirmados = asistencia.count { it.value }
                    val progreso = if (viaje.totalAsientos > 0) confirmados.toFloat() / viaje.totalAsientos else 0f

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            "$confirmados/${viaje.totalAsientos} abordaron",
                            color = Color(0xFF81C784),
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp
                        )
                        Text(
                            "${(progreso * 100).toInt()}%",
                            color = Color(0xFF81C784),
                            fontWeight = FontWeight.Bold,
                            fontSize = 13.sp
                        )
                    }
                    Spacer(modifier = Modifier.height(6.dp))
                    LinearProgressIndicator(
                        progress = { progreso },
                        modifier = Modifier.fillMaxWidth().height(6.dp).clip(RoundedCornerShape(3.dp)),
                        color = Color(0xFF81C784),
                        trackColor = Color.White.copy(alpha = 0.15f)
                    )
                }
            }

            // ========== BOTÓN INICIAR/FINALIZAR VIAJE ==========
            // Ocultos en modo espectador (admin viendo panel de un chofer): el admin no
            // debe poder iniciar/finalizar el viaje de otro; queda visible solo el manifiesto.
            if (viaje.esHoy && !esEspectador) {
                Spacer(modifier = Modifier.height(12.dp))

                when (estadoViaje) {
                    "no_iniciado" -> {
                        Button(
                            onClick = { mostrarConfirmIniciar = true },
                            modifier = Modifier.fillMaxWidth().height(52.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Icon(Icons.Filled.PlayArrow, contentDescription = null, tint = Color.White, modifier = Modifier.size(22.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text("Iniciar Viaje", fontWeight = FontWeight.Bold, color = Color.White, fontSize = 16.sp)
                        }
                    }
                    "en_curso" -> {
                        Card(
                            colors = CardDefaults.cardColors(containerColor = SmartVanColors.AsientoDisponible),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Box(
                                        modifier = Modifier.size(10.dp).background(SmartVanColors.Verde, CircleShape)
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text("VIAJE EN CURSO", fontWeight = FontWeight.Black, color = SmartVanColors.VerdeOscuro, fontSize = 14.sp)
                                    Spacer(modifier = Modifier.weight(1f))
                                    if (ultimaUbicacion != null) {
                                        Text(
                                            "GPS activo",
                                            fontSize = 11.sp,
                                            color = SmartVanColors.Verde,
                                            fontWeight = FontWeight.Medium
                                        )
                                    }
                                }
                                Spacer(modifier = Modifier.height(8.dp))
                                Button(
                                    onClick = { mostrarConfirmFinalizar = true },
                                    modifier = Modifier.fillMaxWidth().height(44.dp),
                                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFEF5350)),
                                    shape = RoundedCornerShape(10.dp)
                                ) {
                                    Icon(Icons.Filled.Stop, contentDescription = null, tint = Color.White, modifier = Modifier.size(20.dp))
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text("Finalizar Viaje", fontWeight = FontWeight.Bold, color = Color.White)
                                }
                            }
                        }
                    }
                    "finalizado" -> {
                        Card(
                            colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoGrisClaro),
                            shape = RoundedCornerShape(12.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Row(modifier = Modifier.padding(14.dp), verticalAlignment = Alignment.CenterVertically) {
                                Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(22.dp))
                                Spacer(modifier = Modifier.width(8.dp))
                                Text("Viaje finalizado", fontWeight = FontWeight.Bold, color = Color.Gray, fontSize = 14.sp)
                            }
                        }
                    }
                }
            }

            // Diálogo confirmar inicio
            if (mostrarConfirmIniciar) {
                AlertDialog(
                    onDismissRequest = { mostrarConfirmIniciar = false },
                    icon = { Icon(Icons.Filled.PlayArrow, contentDescription = null, tint = SmartVanColors.Verde, modifier = Modifier.size(32.dp)) },
                    title = { Text("Iniciar Viaje", fontWeight = FontWeight.Bold) },
                    text = {
                        Column {
                            Text("Se activará el rastreo GPS para que los pasajeros puedan seguir el viaje en tiempo real.")
                            Spacer(modifier = Modifier.height(8.dp))
                            Text("Ruta: ${viaje.ruta}", fontWeight = FontWeight.Medium)
                            Text("Horario: ${viaje.horario}", fontWeight = FontWeight.Medium)
                            Spacer(modifier = Modifier.height(8.dp))
                            Text("Tu navegador pedirá permiso de ubicación.", fontSize = 12.sp, color = Color.Gray)
                        }
                    },
                    confirmButton = {
                        Button(
                            onClick = {
                                mostrarConfirmIniciar = false
                                viewModel.iniciarViaje(viaje)
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde)
                        ) { Text("Iniciar") }
                    },
                    dismissButton = {
                        TextButton(onClick = { mostrarConfirmIniciar = false }) { Text("Cancelar") }
                    }
                )
            }

            // Diálogo confirmar finalización
            if (mostrarConfirmFinalizar) {
                AlertDialog(
                    onDismissRequest = { mostrarConfirmFinalizar = false },
                    icon = { Icon(Icons.Filled.Stop, contentDescription = null, tint = Color(0xFFEF5350), modifier = Modifier.size(32.dp)) },
                    title = { Text("Finalizar Viaje", fontWeight = FontWeight.Bold) },
                    text = { Text("Se detendrá el rastreo GPS y los pasajeros verán que el viaje ha concluido.") },
                    confirmButton = {
                        Button(
                            onClick = {
                                mostrarConfirmFinalizar = false
                                viewModel.finalizarViaje(viaje)
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFEF5350))
                        ) { Text("Finalizar") }
                    },
                    dismissButton = {
                        TextButton(onClick = { mostrarConfirmFinalizar = false }) { Text("Cancelar") }
                    }
                )
            }

            Spacer(modifier = Modifier.height(12.dp))

            // QR scan button — oculto en modo espectador (verificar es acción del chofer).
            if (!esEspectador) {
                Button(
                    onClick = { mostrarEscaner = true },
                    modifier = Modifier.fillMaxWidth().height(50.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = smartvanCyan),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Icon(Icons.Filled.CameraAlt, contentDescription = null, tint = Color.White, modifier = Modifier.size(20.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Escanear QR del Boleto", fontWeight = FontWeight.Bold, color = Color.White)
                }
            }

            Spacer(modifier = Modifier.height(8.dp))

            // Manual code button
            OutlinedButton(
                onClick = { mostrarInputManual = !mostrarInputManual },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.outlinedButtonColors(contentColor = smartvanDark)
            ) {
                Icon(Icons.Filled.Edit, contentDescription = null, modifier = Modifier.size(18.dp))
                Spacer(modifier = Modifier.width(8.dp))
                Text("Verificar código manual")
            }

            if (mostrarInputManual) {
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    OutlinedTextField(
                        value = codigoManual,
                        onValueChange = { codigoManual = it },
                        label = { Text("SMARTVAN-...") },
                        placeholder = { Text("1747095000000-5") },
                        leadingIcon = {
                            Text(
                                "SV-",
                                fontWeight = FontWeight.Bold,
                                color = smartvanCyan,
                                fontSize = 12.sp
                            )
                        },
                        modifier = Modifier.weight(1f),
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = smartvanCyan,
                            focusedLabelColor = smartvanCyan,
                            cursorColor = smartvanCyan
                        )
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Button(
                        onClick = {
                            scope.launch {
                                val codigoCompleto = if (codigoManual.trim().startsWith("SMARTVAN-")) {
                                    codigoManual.trim()
                                } else {
                                    "SMARTVAN-${codigoManual.trim()}"
                                }
                                val resultado = viewModel.verificarCodigoQR(codigoCompleto)
                                mostrarResultadoQR = resultado
                                if (resultado is VerificacionResult.Exito) {
                                    asistencia = asistencia + (resultado.asiento to true)
                                }
                                if (resultado is VerificacionResult.PagoPendiente) {
                                    asistencia = asistencia + (resultado.asiento to true)
                                }
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = smartvanDark),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text("Verificar")
                    }
                }
            }

            // QR Result card with icons
            mostrarResultadoQR?.let { resultado ->
                Spacer(modifier = Modifier.height(8.dp))

                val (bgColor, iconColor, icon, titulo) = when (resultado) {
                    is VerificacionResult.Exito -> listOf(
                        SmartVanColors.AsientoDisponible, SmartVanColors.Verde, Icons.Filled.CheckCircle, "Boleto válido - Asiento ${resultado.asiento}"
                    )
                    is VerificacionResult.PagoPendiente -> listOf(
                        SmartVanColors.FondoAmarillo, Color(0xFFFF6F00), Icons.Filled.AttachMoney, "PAGO PENDIENTE - Asiento ${resultado.asiento}"
                    )
                    is VerificacionResult.YaVerificado -> listOf(
                        SmartVanColors.FondoNaranja, SmartVanColors.Naranja, Icons.Filled.Warning, "Ya abordó - Asiento ${resultado.asiento}"
                    )
                    is VerificacionResult.Error -> listOf(
                        SmartVanColors.FondoRojo, Color.Red, Icons.Filled.Cancel, resultado.mensaje
                    )
                }

                Card(
                    colors = CardDefaults.cardColors(containerColor = bgColor as Color),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(14.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(
                            modifier = Modifier
                                .size(40.dp)
                                .background((iconColor as Color).copy(alpha = 0.15f), CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(icon as ImageVector, contentDescription = null, tint = iconColor, modifier = Modifier.size(24.dp))
                        }
                        Spacer(modifier = Modifier.width(12.dp))
                        Column {
                            Text(
                                titulo as String,
                                fontWeight = FontWeight.Bold,
                                color = iconColor,
                                fontSize = 14.sp
                            )
                            if (resultado is VerificacionResult.Exito) {
                                Text("Pasajero: ${resultado.pasajero}", fontSize = 13.sp, color = Color.Gray)
                            }
                            if (resultado is VerificacionResult.PagoPendiente) {
                                Text("Pasajero: ${resultado.pasajero}", fontSize = 13.sp, color = Color.Gray)
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(
                                    "Este pasajero debe pagar en EFECTIVO al abordar",
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFFFF6F00)
                                )
                            }
                            if (resultado is VerificacionResult.YaVerificado) {
                                Text("Pasajero: ${resultado.pasajero}", fontSize = 13.sp, color = Color.Gray)
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            if (viaje.pasajeros.isEmpty()) {
                Card(
                    colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoNaranja),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(modifier = Modifier.padding(20.dp), verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.Info, contentDescription = null, tint = SmartVanColors.NaranjaDeep, modifier = Modifier.size(22.dp))
                        Spacer(modifier = Modifier.width(12.dp))
                        Text("No hay pasajeros registrados.", color = SmartVanColors.NaranjaDeep)
                    }
                }
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Pasajeros (${viaje.pasajeros.size})", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = smartvanDark)
                }
                Spacer(modifier = Modifier.height(8.dp))

                // Mapa visual del vehículo con estado por asiento (comprado / abordó / debe pago / vacío).
                val layoutViaje by viewModel.layoutViajeSeleccionado.collectAsState()
                LaunchedEffect(viaje.ruta, viaje.fecha, viaje.horario) {
                    viewModel.cargarLayoutDelViaje(viaje.ruta, viaje.fecha, viaje.horario)
                }
                val listaConPago = viaje.pasajerosConPago.ifEmpty {
                    viaje.pasajeros.map { PasajeroConPago(pasajero = it) }
                }
                val estadoAsiento: Map<Int, EstadoAsientoChofer> = listaConPago.associate { pcp ->
                    val a = pcp.pasajero.asiento
                    val abordado = asistencia[a] == true
                    val estado = when {
                        abordado -> EstadoAsientoChofer.ABORDADO
                        pcp.metodoPago == "efectivo" && !pcp.pagoConfirmado -> EstadoAsientoChofer.PENDIENTE_PAGO
                        else -> EstadoAsientoChofer.COMPRADO
                    }
                    a to estado
                }

                // Filtro por asiento (click en el layout) + agrupación por subida/bajada.
                var asientoFiltro by remember(viaje.horario) { mutableStateOf<Int?>(null) }
                var modoVista by remember(viaje.horario) { mutableStateOf("TODOS") } // TODOS | SUBIDA | BAJADA

                Card(
                    colors = CardDefaults.cardColors(containerColor = Color.White),
                    shape = RoundedCornerShape(14.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(12.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                        MapaAsientosChofer(
                            vehiculo = layoutViaje,
                            estadoAsiento = estadoAsiento,
                            seleccionado = asientoFiltro,
                            onAsientoClick = { a ->
                                asientoFiltro = if (asientoFiltro == a) null else a
                            }
                        )
                        if (asientoFiltro != null) {
                            Spacer(modifier = Modifier.height(6.dp))
                            TextButton(onClick = { asientoFiltro = null }) {
                                Text("Ver todos los pasajeros", color = SmartVanColors.AzulOscuro, fontSize = 12.sp)
                            }
                        }
                    }
                }
                Spacer(modifier = Modifier.height(10.dp))

                // Chips: filtros de agrupación (útiles antes/durante el viaje).
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    FilterChip(
                        selected = modoVista == "TODOS",
                        onClick = { modoVista = "TODOS" },
                        label = { Text("Todos") }
                    )
                    FilterChip(
                        selected = modoVista == "SUBIDA",
                        onClick = { modoVista = "SUBIDA" },
                        label = { Text("Por subida") }
                    )
                    FilterChip(
                        selected = modoVista == "BAJADA",
                        onClick = { modoVista = "BAJADA" },
                        label = { Text("Por bajada") }
                    )
                }
                Spacer(modifier = Modifier.height(8.dp))

                // Lista visible: si hay asiento seleccionado, solo ese pasajero. Si no, aplica el modoVista.
                val listaVisible: List<PasajeroConPago> = if (asientoFiltro != null) {
                    listaConPago.filter { it.pasajero.asiento == asientoFiltro }
                } else listaConPago

                // Diálogo de confirmación de cobro efectivo (evita error de dedo).
                var confirmarCobroPara by remember(viaje.horario) { mutableStateOf<PasajeroConPago?>(null) }
                @Composable
                fun cardPasajero(pcp: PasajeroConPago) {
                    val asistio = asistencia[pcp.pasajero.asiento] ?: false
                    PasajeroChoferCard(
                        pasajero = pcp.pasajero,
                        asistio = asistio,
                        metodoPago = pcp.metodoPago,
                        pagoConfirmado = pcp.pagoConfirmado,
                        contextoViaje = "${viaje.ruta} ${viaje.fecha} ${viaje.horario}",
                        timestampReserva = pcp.timestampReserva,
                        onToggleAsistencia = if (esEspectador) null else { nuevoValor ->
                            scope.launch {
                                viewModel.marcarAsistenciaManual(viaje, pcp.pasajero.asiento, nuevoValor)
                                asistencia = asistencia + (pcp.pasajero.asiento to nuevoValor)
                            }
                        },
                        onCobrarEfectivo = if (!esEspectador && pcp.metodoPago == "efectivo" && !pcp.pagoConfirmado && pcp.reservaId.isNotBlank()) {
                            { confirmarCobroPara = pcp }
                        } else null
                    )
                }

                confirmarCobroPara?.let { pcp ->
                    AlertDialog(
                        onDismissRequest = { confirmarCobroPara = null },
                        icon = { Icon(Icons.Filled.Payments, contentDescription = null, tint = SmartVanColors.Verde) },
                        title = { Text("Confirmar cobro en efectivo") },
                        text = {
                            Column {
                                Text("Vas a marcar como PAGADO el asiento ${pcp.pasajero.asiento} de ${pcp.pasajero.nombre.ifBlank { "—" }}.", style = MaterialTheme.typography.bodySmall)
                                Spacer(modifier = Modifier.height(6.dp))
                                Text("Confirma solo si YA recibiste el dinero. No se puede revertir sin admin.",
                                    style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                                Spacer(modifier = Modifier.height(6.dp))
                                Text("Nota: solo puedes cobrar desde 1h antes del viaje.",
                                    style = MaterialTheme.typography.labelSmall, color = Color(0xFFEF6C00))
                            }
                        },
                        confirmButton = {
                            Button(
                                onClick = {
                                    val target = pcp
                                    confirmarCobroPara = null
                                    viewModel.confirmarPagoEfectivo(viaje, target.reservaId) { _, _ -> }
                                },
                                colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde)
                            ) { Text("Sí, cobrado") }
                        },
                        dismissButton = {
                            TextButton(onClick = { confirmarCobroPara = null }) { Text("Cancelar", color = Color.Gray) }
                        }
                    )
                }

                // Ruta del viaje en Maps: arranca en el primer punto y encadena el resto como
                // waypoints. Solo aparece si al menos 2 puntos del viaje tienen coordenadas.
                @Composable
                fun botonRutaCompleta(subidas: List<String>, bajadas: List<String>) {
                    val url = viewModel.urlRutaCompleta(subidas, bajadas)
                    if (url.isBlank()) return
                    OutlinedButton(
                        onClick = { uriHandlerChofer.openUri(url) },
                        modifier = Modifier.fillMaxWidth().height(38.dp),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Icon(Icons.Filled.Map, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text("Abrir ruta completa en Maps", fontSize = 12.sp, color = SmartVanColors.AzulOscuro, fontWeight = FontWeight.Bold)
                    }
                }

                // nombrePunto: para abrir ese lugar en Maps. "" = grupo sin lugar real
                // ("Por definir", "Sin bajada asignada") → sin link.
                @Composable
                fun headerGrupo(
                    icono: androidx.compose.ui.graphics.vector.ImageVector,
                    titulo: String,
                    cantidad: Int,
                    nombrePunto: String = ""
                ) {
                    val urlPunto = mapsPorPunto[nombrePunto].orEmpty()
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth().padding(top = 8.dp, bottom = 4.dp)
                    ) {
                        Icon(icono, contentDescription = null, tint = SmartVanColors.AzulOscuro, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(6.dp))
                        Text(
                            titulo, fontWeight = FontWeight.Bold, color = SmartVanColors.AzulOscuro,
                            style = MaterialTheme.typography.bodyMedium,
                            maxLines = 1, overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f, fill = false)
                        )
                        if (urlPunto.isNotBlank()) {
                            Spacer(modifier = Modifier.width(6.dp))
                            TextButton(
                                onClick = { uriHandlerChofer.openUri(urlPunto) },
                                contentPadding = PaddingValues(horizontal = 6.dp, vertical = 0.dp),
                                modifier = Modifier.height(24.dp)
                            ) {
                                Icon(Icons.Filled.LocationOn, contentDescription = "Abrir en Maps", tint = SmartVanColors.CyanAccent, modifier = Modifier.size(14.dp))
                                Spacer(modifier = Modifier.width(2.dp))
                                Text("Maps", fontSize = 11.sp, color = SmartVanColors.CyanAccent, fontWeight = FontWeight.Bold)
                            }
                        }
                        Spacer(modifier = Modifier.weight(1f))
                        Text("$cantidad", color = Color.Gray, style = MaterialTheme.typography.labelSmall)
                    }
                }

                // Column + forEach (no LazyColumn) porque el padre ya scrollea verticalmente.
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    when (modoVista) {
                        "SUBIDA" -> {
                            // Agrupar por el puntoSubida DE LA RESERVA. Si la reserva no trae uno
                            // propio, cae al del viaje. Tolerancia legacy: si el viaje tiene múltiples
                            // puntos concatenados con "|" (bug viejo), NO los usamos como grupo
                            // (mostramos "Por definir" en su lugar). Nuevas reservas ya no cargan pipe.
                            val fallbackViaje = if ("|" in viaje.puntoSubida) "" else viaje.puntoSubida
                            val grupos = listaVisible.groupBy {
                                it.puntoSubida.ifBlank { fallbackViaje.ifBlank { "Punto de subida por definir" } }
                            }
                            val puntosOrdenados = grupos.keys.sorted()
                            botonRutaCompleta(puntosOrdenados, emptyList())
                            puntosOrdenados.forEach { punto ->
                                val lista = grupos[punto].orEmpty()
                                headerGrupo(Icons.Filled.TripOrigin, "Sube en: $punto", lista.size, punto)
                                lista.forEach { cardPasajero(it) }
                            }
                        }
                        "BAJADA" -> {
                            val grupos = listaVisible.groupBy {
                                it.pasajero.parada.ifBlank { "Sin bajada asignada" }
                            }
                            val paradasOrdenadas = grupos.keys.sorted()
                            botonRutaCompleta(emptyList(), paradasOrdenadas)
                            paradasOrdenadas.forEach { parada ->
                                val lista = grupos[parada].orEmpty()
                                headerGrupo(Icons.Filled.LocationOn, "Baja en: $parada", lista.size, parada)
                                lista.forEach { cardPasajero(it) }
                            }
                        }
                        else -> {
                            listaVisible.forEach { cardPasajero(it) }
                        }
                    }
                    if (listaVisible.isEmpty()) {
                        Text(
                            "Sin pasajeros con ese filtro.",
                            color = Color.Gray, style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(12.dp)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun PasajeroChoferCard(pasajero: PasajeroInfo, asistio: Boolean = false, metodoPago: String = "", pagoConfirmado: Boolean = true, contextoViaje: String = "", timestampReserva: Long = 0L, onToggleAsistencia: ((Boolean) -> Unit)? = null, onCobrarEfectivo: (() -> Unit)? = null) {
    val smartvanCyan = SmartVanColors.CyanAccent
    val uriHandler = LocalUriHandler.current

    Card(
        colors = CardDefaults.cardColors(
            containerColor = if (asistio) SmartVanColors.AsientoDisponible else Color.White
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        shape = RoundedCornerShape(12.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (onToggleAsistencia != null) {
                Checkbox(
                    checked = asistio,
                    onCheckedChange = { onToggleAsistencia(it) },
                    colors = CheckboxDefaults.colors(
                        checkedColor = SmartVanColors.Verde,
                        uncheckedColor = Color.LightGray
                    )
                )
            }

            Box(
                modifier = Modifier
                    .size(44.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .background(if (asistio) SmartVanColors.Verde else smartvanCyan),
                contentAlignment = Alignment.Center
            ) {
                Text("${pasajero.asiento}", color = Color.White, fontWeight = FontWeight.Black, fontSize = 18.sp)
            }

            Spacer(modifier = Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    pasajero.nombre.ifBlank { "Sin nombre" },
                    fontWeight = FontWeight.Bold,
                    fontSize = 15.sp,
                    color = SmartVanColors.AzulOscuro
                )
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Filled.Phone, contentDescription = null, modifier = Modifier.size(13.dp), tint = Color.Gray)
                    Spacer(modifier = Modifier.width(4.dp))
                    Text(pasajero.telefono.ifBlank { "Sin teléfono" }, color = Color.Gray, fontSize = 12.sp)
                    if (pasajero.telefono.isNotBlank()) {
                        Spacer(modifier = Modifier.width(6.dp))
                        OutlinedButton(
                            onClick = {
                                val ctx = if (contextoViaje.isNotBlank()) " sobre su viaje $contextoViaje" else ""
                                val msg = "Hola, le escribo de SmartVan$ctx. Le llamo para coordinar el abordaje."
                                uriHandler.openUri(construirWhatsAppUrl(pasajero.telefono, msg))
                            },
                            modifier = Modifier.height(26.dp),
                            contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 8.dp, vertical = 0.dp),
                            border = BorderStroke(1.dp, Color(0xFF25D366))
                        ) {
                            Icon(Icons.Filled.Chat, contentDescription = null, tint = Color(0xFF25D366), modifier = Modifier.size(12.dp))
                            Spacer(modifier = Modifier.width(3.dp))
                            Text("WhatsApp", fontSize = 10.sp, color = Color(0xFF25D366))
                        }
                    }
                }
                if (pasajero.parada.isNotBlank()) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Filled.LocationOn, contentDescription = null, modifier = Modifier.size(13.dp), tint = smartvanCyan)
                        Spacer(modifier = Modifier.width(4.dp))
                        Text("Baja en: ${pasajero.parada}", color = smartvanCyan, fontSize = 12.sp)
                    }
                }
            }

            Column(horizontalAlignment = Alignment.End) {
                if (asistio) {
                    Box(
                        modifier = Modifier
                            .background(SmartVanColors.Verde.copy(alpha = 0.1f), RoundedCornerShape(20.dp))
                            .padding(horizontal = 8.dp, vertical = 3.dp)
                    ) {
                        Text("Abordó", fontSize = 10.sp, color = SmartVanColors.Verde, fontWeight = FontWeight.Bold)
                    }
                }
                if (pasajero.llevaMascota) {
                    Spacer(modifier = Modifier.height(2.dp))
                    Box(
                        modifier = Modifier
                            .background(SmartVanColors.Naranja.copy(alpha = 0.1f), RoundedCornerShape(20.dp))
                            .padding(horizontal = 8.dp, vertical = 3.dp)
                    ) {
                        Text("Mascota", fontSize = 10.sp, color = SmartVanColors.Naranja, fontWeight = FontWeight.Bold)
                    }
                }
                if (pasajero.maletasExtras > 0) {
                    Spacer(modifier = Modifier.height(2.dp))
                    Box(
                        modifier = Modifier
                            .background(SmartVanColors.Purpura.copy(alpha = 0.1f), RoundedCornerShape(20.dp))
                            .padding(horizontal = 8.dp, vertical = 3.dp)
                    ) {
                        Text("${pasajero.maletasExtras} maleta(s)", fontSize = 10.sp, color = SmartVanColors.Purpura, fontWeight = FontWeight.Bold)
                    }
                }
                if (metodoPago == "efectivo") {
                    Spacer(modifier = Modifier.height(2.dp))
                    Box(
                        modifier = Modifier
                            .background(
                                if (pagoConfirmado) SmartVanColors.Verde.copy(alpha = 0.1f) else Color(0xFFEF5350).copy(alpha = 0.1f),
                                RoundedCornerShape(20.dp)
                            )
                            .padding(horizontal = 8.dp, vertical = 3.dp)
                    ) {
                        Text(
                            if (pagoConfirmado) "Efectivo pagado" else "Pago Pendiente",
                            fontSize = 10.sp,
                            color = if (pagoConfirmado) SmartVanColors.Verde else Color(0xFFEF5350),
                            fontWeight = FontWeight.Bold
                        )
                    }
                    // Botón "Cobrar" para chofer/admin — visible cuando efectivo aún no pagado.
                    // La CF valida rol y guard de 1h en el servidor (defensa dura).
                    if (!pagoConfirmado && onCobrarEfectivo != null) {
                        Spacer(modifier = Modifier.height(4.dp))
                        Button(
                            onClick = onCobrarEfectivo,
                            colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde),
                            modifier = Modifier.height(30.dp),
                            contentPadding = PaddingValues(horizontal = 10.dp, vertical = 0.dp)
                        ) {
                            Text("Cobrar", color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.Bold)
                        }
                    }

                    // Badge "Revisar" cuando la reserva en efectivo lleva >7 días sin cobrar.
                    // Umbral generoso: el cliente puede reservar con anticipación y pagar el
                    // mismo día del viaje. Solo alertamos si ya es raramente antigua.
                    val umbral7d = 7L * 24L * 60L * 60L * 1000L
                    val esVieja = !pagoConfirmado &&
                        timestampReserva > 0L &&
                        (org.example.abigail.obtenerMilisegundosActuales() - timestampReserva) > umbral7d
                    if (esVieja) {
                        Spacer(modifier = Modifier.height(2.dp))
                        Box(
                            modifier = Modifier
                                .background(Color(0xFFFFA726).copy(alpha = 0.15f), RoundedCornerShape(20.dp))
                                .padding(horizontal = 8.dp, vertical = 3.dp)
                        ) {
                            Text(
                                "Revisar (7d+)",
                                fontSize = 10.sp,
                                color = Color(0xFFEF6C00),
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ResumenCard(modifier: Modifier, titulo: String, cantidad: Int, color: Color, icon: ImageVector) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        shape = RoundedCornerShape(14.dp)
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .size(32.dp)
                    .background(color.copy(alpha = 0.1f), CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(icon, contentDescription = null, tint = color, modifier = Modifier.size(18.dp))
            }
            Spacer(modifier = Modifier.height(6.dp))
            Text(
                "$cantidad",
                fontSize = 26.sp,
                fontWeight = FontWeight.Black,
                color = color
            )
            Text(titulo, fontSize = 11.sp, color = Color.Gray)
        }
    }
}
